import type { ActiveElement, Chart, TooltipModel } from "chart.js";

import { isFiniteNumber } from "../../../../utils/number";

import type { IChartWithCrosshair } from "../external-tooltip";
import { getViewportTransform, mapPointToViewport } from "../selection-zoom/geometry";
import type { IViewportZoomBounds } from "../selection-zoom/types";

type DataPoint = {
  x: number;
  y: number;
};

type ChartAxisScale = {
  getPixelForValue: (value: number) => number;
};

type SyncedPoint = DataPoint & {
  datasetIndex: number;
  index: number;
  nativeX: number;
  pixelX: number;
  pixelY: number;
  element?: { x?: number; y?: number };
};

type DatasetRange = {
  datasetIndex: number;
  min: number;
  max: number;
};

type TooltipController = TooltipModel<"line" | "radar"> & {
  setActiveElements?(active: ActiveElement[], eventPosition: { x: number; y: number }): void;
  _active?: ActiveElement[];
  update?(change?: boolean): void;
};

export interface ISyncedCrosshairValues {
  cursorValues: readonly (number | null)[] | null;
  markerValues: readonly (number | null)[] | null;
  cursorDatasetIndex?: number | null;
  markerDatasetIndex?: number | null;
}

const getDataPoint = (point: unknown): DataPoint | null => {
  if (!point || typeof point !== "object") {
    return null;
  }

  const candidate = point as Record<string, unknown>;

  return isFiniteNumber(candidate.x) && isFiniteNumber(candidate.y) ? { x: candidate.x, y: candidate.y } : null;
};

const getXOffset = (chart: IChartWithCrosshair): number => {
  const options = ((chart.options?.plugins || {}) as { crosshair?: { xOffset?: number } }).crosshair;

  return isFiniteNumber(options?.xOffset) ? options.xOffset : 0;
};

const getAxisScale = (chart: Chart, axisId: string | undefined, fallback: "x" | "y"): ChartAxisScale | null => {
  const scales = chart.scales as Record<string, ChartAxisScale | undefined>;
  const scale = (axisId ? scales[axisId] : undefined) ?? scales[fallback];

  return scale && typeof scale.getPixelForValue === "function" ? scale : null;
};

const isPointVisible = (chart: IChartWithCrosshair, x: number, y: number): boolean => {
  if (!isFiniteNumber(x) || !isFiniteNumber(y)) {
    return false;
  }

  const area = chart.chartArea;

  if (area && (x < area.left || x > area.right || y < area.top || y > area.bottom)) {
    return false;
  }

  const options = chart.config?.options?.plugins as
    | { selectionZoom?: { viewportMode?: boolean; viewport?: IViewportZoomBounds } }
    | undefined;
  const viewport = options?.selectionZoom;
  const transform = viewport?.viewportMode
    ? getViewportTransform({ left: 0, right: chart.width, top: 0, bottom: chart.height }, viewport.viewport)
    : null;

  if (!transform) {
    return true;
  }

  const point = mapPointToViewport({ x, y }, transform);

  return point.x >= 0 && point.x <= chart.width && point.y >= 0 && point.y <= chart.height;
};

const applyTooltipActiveElements = (
  chart: IChartWithCrosshair,
  activeElements: ActiveElement[],
  position: { x: number; y: number },
): void => {
  const tooltip = chart.tooltip as TooltipController | undefined;

  if (typeof chart.setActiveElements === "function") {
    chart.setActiveElements([]);
  }

  if (!tooltip) {
    return;
  }

  if (typeof tooltip.setActiveElements === "function") {
    tooltip.setActiveElements(activeElements, position);
  } else {
    tooltip._active = activeElements;
  }

  if (typeof tooltip.update === "function") {
    tooltip.update(true);
  }
};

const getDatasetRanges = (chart: IChartWithCrosshair): DatasetRange[] => {
  const ranges: DatasetRange[] = [];
  const xOffset = getXOffset(chart);

  chart.data.datasets.forEach((dataset, datasetIndex) => {
    if (chart.getDatasetMeta(datasetIndex).hidden || !Array.isArray(dataset.data)) {
      return;
    }

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    for (const rawPoint of dataset.data) {
      const point = getDataPoint(rawPoint);

      if (point) {
        min = Math.min(min, point.x - datasetIndex * xOffset);
        max = Math.max(max, point.x - datasetIndex * xOffset);
      }
    }

    if (min <= max) {
      ranges.push({ datasetIndex, min, max });
    }
  });

  return ranges;
};

const findPointInDataset = (chart: IChartWithCrosshair, datasetIndex: number, nativeX: number): SyncedPoint | null => {
  const dataset = chart.data.datasets[datasetIndex];
  const meta = chart.getDatasetMeta(datasetIndex);
  const xScale = getAxisScale(chart, meta.xAxisID, "x");
  const yScale = getAxisScale(chart, meta.yAxisID, "y");

  if (!xScale || !yScale || !Array.isArray(dataset.data)) {
    return null;
  }

  const x = nativeX + datasetIndex * getXOffset(chart);
  let previous: (DataPoint & { index: number }) | null = null;

  for (let index = 0; index < dataset.data.length; index += 1) {
    const point = getDataPoint(dataset.data[index]);

    if (!point) {
      previous = null;
      continue;
    }

    let y: number | null = null;
    let nearestIndex = index;

    if (point.x === x) {
      y = point.y;
    } else if (previous && x > Math.min(previous.x, point.x) && x < Math.max(previous.x, point.x)) {
      y = previous.y + ((x - previous.x) / (point.x - previous.x)) * (point.y - previous.y);
      nearestIndex = Math.abs(x - previous.x) <= Math.abs(x - point.x) ? previous.index : index;
    }

    if (y !== null) {
      const pixelX = xScale.getPixelForValue(x);
      const pixelY = yScale.getPixelForValue(y);

      if (isPointVisible(chart, pixelX, pixelY)) {
        return {
          datasetIndex,
          index: nearestIndex,
          nativeX,
          x,
          y,
          pixelX,
          pixelY,
          element: meta.data?.[nearestIndex] as { x?: number; y?: number } | undefined,
        };
      }
    }

    previous = { ...point, index };
  }

  return null;
};

const chooseRange = (
  ranges: DatasetRange[],
  values: readonly (number | null)[] | null,
  preferredDatasetIndex?: number | null,
): DatasetRange | null => {
  const available = ranges.filter(range => isFiniteNumber(values?.[range.datasetIndex]));

  return available.find(range => range.datasetIndex === preferredDatasetIndex) ?? available[0] ?? null;
};

const resetCrosshair = (chart: IChartWithCrosshair) => {
  const crosshair = (chart.crosshair ??= {});

  crosshair.locked = false;
  crosshair.lockedX = null;
  crosshair.lockedActiveElements = null;
  crosshair.snappedActiveElements = null;
  crosshair.enabled = false;
  crosshair.x = null;
  crosshair.baseValue = null;
  crosshair.baseDatasetIndex = null;
  crosshair.snapPixelY = null;
  crosshair.markerValue = null;
  crosshair.markerDatasetIndex = null;
  crosshair.syncCursorValues = null;
  crosshair.syncMarkerValues = null;
  crosshair.syncDatasetRanges = null;
  crosshair.syncedPoint = null;
  crosshair.syncBand = null;
  crosshair.syncMarkerInRange = false;
  chart._lockedTooltip = null;
  chart._externalTooltipPoint = null;

  return crosshair;
};

export const applySyncedCrosshair = (chart: IChartWithCrosshair, values: ISyncedCrosshairValues): boolean => {
  const crosshair = resetCrosshair(chart);
  const ranges = getDatasetRanges(chart);
  const { cursorValues, markerValues, cursorDatasetIndex, markerDatasetIndex } = values;
  const preferredRange = chooseRange(ranges, cursorValues, cursorDatasetIndex);
  const orderedRanges = preferredRange ? [preferredRange, ...ranges.filter(range => range !== preferredRange)] : ranges;
  let point: SyncedPoint | null = null;

  for (const range of orderedRanges) {
    const nativeX = cursorValues?.[range.datasetIndex];

    if (isFiniteNumber(nativeX) && nativeX >= range.min && nativeX <= range.max) {
      point = findPointInDataset(chart, range.datasetIndex, nativeX);

      if (point) {
        break;
      }
    }
  }

  crosshair.syncCursorValues = cursorValues;
  crosshair.syncMarkerValues = markerValues;
  crosshair.syncDatasetRanges = ranges;

  const inRangeMarkers = ranges.filter(range => {
    const nativeX = markerValues?.[range.datasetIndex];

    return isFiniteNumber(nativeX) && nativeX >= range.min && nativeX <= range.max;
  });
  const markerRange = chooseRange(
    inRangeMarkers.length > 0 ? inRangeMarkers : ranges,
    markerValues,
    markerDatasetIndex ?? point?.datasetIndex,
  );

  if (markerRange) {
    const markerValue = markerValues?.[markerRange.datasetIndex];

    if (isFiniteNumber(markerValue)) {
      crosshair.markerValue = markerValue;
      crosshair.markerDatasetIndex = markerRange.datasetIndex;
      crosshair.syncMarkerInRange = markerValue >= markerRange.min && markerValue <= markerRange.max;
    }
  }

  const bandRanges = ranges.filter(range => {
    const cursorValue = cursorValues?.[range.datasetIndex];
    const markerValue = markerValues?.[range.datasetIndex];

    return (
      isFiniteNumber(cursorValue) &&
      isFiniteNumber(markerValue) &&
      Math.max(range.min, Math.min(cursorValue, markerValue)) < Math.min(range.max, Math.max(cursorValue, markerValue))
    );
  });
  const bandRange = chooseRange(
    bandRanges,
    cursorValues,
    point?.datasetIndex ?? cursorDatasetIndex ?? markerRange?.datasetIndex,
  );

  if (bandRange) {
    const cursorValue = cursorValues?.[bandRange.datasetIndex];
    const markerValue = markerValues?.[bandRange.datasetIndex];

    if (isFiniteNumber(cursorValue) && isFiniteNumber(markerValue)) {
      const min = Math.max(bandRange.min, Math.min(cursorValue, markerValue));
      const max = Math.min(bandRange.max, Math.max(cursorValue, markerValue));

      if (min < max) {
        crosshair.syncBand = { datasetIndex: bandRange.datasetIndex, min, max };
      }
    }
  }

  const activeElements = point
    ? [{ datasetIndex: point.datasetIndex, index: point.index, element: point.element } as ActiveElement]
    : [];
  const position = point ? { x: point.pixelX, y: point.pixelY } : { x: 0, y: 0 };

  if (point) {
    crosshair.enabled = true;
    crosshair.locked = true;
    crosshair.lockedX = point.pixelX;
    crosshair.x = point.pixelX;
    crosshair.baseValue = point.nativeX;
    crosshair.baseDatasetIndex = point.datasetIndex;
    crosshair.lockedActiveElements = activeElements;
    crosshair.snappedActiveElements = activeElements;
    crosshair.snapPixelY = point.pixelY;
    crosshair.syncedPoint = { datasetIndex: point.datasetIndex, nativeX: point.nativeX, x: point.x, y: point.y };
  }

  applyTooltipActiveElements(chart, activeElements, position);
  chart.update("none");
  applyTooltipActiveElements(chart, activeElements, position);
  chart.draw();

  return point !== null;
};

export const applyLockedCrosshairAtNativeX = (
  chart: IChartWithCrosshair,
  nativeX: number,
  preferredDatasetIndex = chart.crosshair?.baseDatasetIndex,
): boolean =>
  applySyncedCrosshair(chart, {
    cursorValues: chart.data.datasets.map(() => nativeX),
    markerValues: chart.crosshair?.syncMarkerValues ?? null,
    cursorDatasetIndex: preferredDatasetIndex,
    markerDatasetIndex: chart.crosshair?.markerDatasetIndex,
  });

export const applyMarkerAtNativeX = (
  chart: IChartWithCrosshair,
  nativeX: number,
  preferredDatasetIndex = chart.crosshair?.markerDatasetIndex,
): boolean => {
  applySyncedCrosshair(chart, {
    cursorValues: chart.crosshair?.syncCursorValues ?? null,
    markerValues: chart.data.datasets.map(() => nativeX),
    cursorDatasetIndex: chart.crosshair?.baseDatasetIndex,
    markerDatasetIndex: preferredDatasetIndex,
  });

  return isFiniteNumber(chart.crosshair?.markerValue);
};

export const clearSyncedCrosshair = (chart: IChartWithCrosshair): void => {
  resetCrosshair(chart);
  applyTooltipActiveElements(chart, [], { x: 0, y: 0 });
  chart.draw();
};
